import { z } from 'zod';

const anxietyStressTraumaSchema = z.object({
    symptomCategory: z.literal('Anxiety, stress, trauma'),
    symptoms: z
        .array(
            z.enum([
                'Anxiety',
                'Avoidance',
                'Difficulty socializing',
                'Fearfulness',
                'Panic attacks',
                'Nightmares',
                'Restlessness',
            ]),
        )
        .optional(),
    other: z.string().trim().optional(),
});

const behavioralDysregulationSchema = z.object({
    symptomCategory: z.literal('Behavioral dysregulation'),
    symptoms: z
        .array(z.enum(['Impulse-control issues', 'Social withdrawal / isolation', 'Substance use']))
        .optional(),
    other: z.string().trim().optional(),
});

const cognitiveDifficultiesSchema = z.object({
    symptomCategory: z.literal('Cognitive difficulties'),
    symptoms: z
        .array(
            z.enum([
                'Emotional dysregulation',
                'Memory issues',
                'Task paralysis',
                'Time blindness',
            ]),
        )
        .optional(),
    other: z.string().trim().optional(),
});

const depressionSchema = z.object({
    symptomCategory: z.literal('Depression'),
    symptoms: z
        .array(
            z.enum([
                'Anhedonia',
                'Appetite Disturbance',
                'Depression',
                'Fatigue',
                'Grief and loss',
                'Guilt',
                'Helplessness',
                'Hopelessness',
                'Lack of motivation',
                'Lethargy',
                'Low self-esteem',
                'Poor sleep',
            ]),
        )
        .optional(),
    other: z.string().trim().optional(),
});

const psychosisSchema = z.object({
    symptomCategory: z.literal('Psychosis'),
    symptoms: z.array(z.enum(['Hallucinations', 'Mania'])).optional(),
    other: z.string().trim().optional(),
});

const relationalInterpersonalSchema = z.object({
    symptomCategory: z.literal('Relational or interpersonal issues'),
    symptoms: z
        .array(
            z.enum([
                'Self identify',
                'Relationship to children',
                'Relationship to family',
                'Relationship to partner',
            ]),
        )
        .optional(),
    other: z.string().trim().optional(),
});

export const managingSymptomsCategorySchema = z.discriminatedUnion('symptomCategory', [
    anxietyStressTraumaSchema,
    behavioralDysregulationSchema,
    cognitiveDifficultiesSchema,
    depressionSchema,
    psychosisSchema,
    relationalInterpersonalSchema,
]);

export const progressSchema = z.object({
    progress: z
        .enum([
            'Significantly progressing',
            'Progressing',
            'Maintaining',
            'Plateauing',
            'Regressing',
        ])
        .optional(),
    progressOther: z.string().trim().optional(),
    improvement: z
        .array(
            z.enum([
                'Awareness of contribution to negative patterns',
                'Awareness of distress tolerance',
                'Awareness of impact of history of traumatic experiences',
                'Awareness of triggers to symptoms',
                'Asserting needs',
                'Emotional regulation',
                'Expression of symptoms',
                'Executive functioning',
                'Providing feedback in relationships',
                'Setting boundaries',
                'Sleep hygiene/routine',
            ]),
        )
        .optional(),
    improvementOther: z.string().trim().optional(),
    managingSymptoms: z.array(managingSymptomsCategorySchema).optional(),
    managingSymptomsOther: z.string().optional(),
    continuesToStruggle: z
        .array(
            z.enum([
                'Communication in relationships',
                'Conflict resolution',
                'Distress tolerance',
                'Emotional regulation',
                'Expressing and asserting needs',
                'Expressing emotions appropriately',
                'Maintaining relationships',
                'Managing work',
                'Motivation',
                'Medication adherence',
                'Repetitive behaviors',
                'Rigid beliefs/patterns',
                'Setting boundaries',
                'Sleep hygiene/routine',
                'Understanding social cues',
                'Using positive coping skills',
            ]),
        )
        .optional(),
    continuesToStruggleOther: z.string().trim().optional(),
    impactsToDailyLife: z
        .array(
            z.enum([
                'Living situation',
                'Physical health',
                'Relationships',
                'School',
                'Self-care',
                'Work',
            ]),
        )
        .optional(),
    impactsToDailyLifeOther: z.string().trim().optional(),
    barriersToProgress: z.string().trim().optional(),
});
